import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Bot, Plus, Search, Loader2, Clock, CheckCircle2, Pause,
    AlertCircle, Archive, MoreHorizontal, Play, Copy, Trash2, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
    draft: { label: 'Draft', color: 'bg-stone-100 text-stone-600', icon: Clock },
    sandbox: { label: 'Sandbox', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
    active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    paused: { label: 'Paused', color: 'bg-blue-100 text-blue-600', icon: Pause },
    archived: { label: 'Archived', color: 'bg-stone-100 text-stone-400', icon: Archive },
};

function AgentCard({ agent, onNavigate, onDuplicate, onDelete }) {
    const [showMenu, setShowMenu] = useState(false);
    const statusConf = STATUS_CONFIG[agent.status] || STATUS_CONFIG.draft;
    const StatusIcon = statusConf.icon;

    return (
        <div
            className="bg-white border border-stone-200 rounded-xl p-5 hover:shadow-md hover:border-stone-300 transition-all cursor-pointer group relative"
            onClick={() => onNavigate(agent.agent_id)}
        >
            {/* Status badge */}
            <div className="flex items-center justify-between mb-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusConf.color}`}>
                    <StatusIcon className="w-3 h-3" /> {statusConf.label}
                </span>
                <div className="relative" onClick={e => e.stopPropagation()}>
                    <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-stone-100 transition-all"
                    >
                        <MoreHorizontal className="w-4 h-4 text-stone-400" />
                    </button>
                    {showMenu && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg py-1 z-20 w-40">
                            <button
                                onClick={() => { setShowMenu(false); onDuplicate(agent.agent_id); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-stone-600 hover:bg-stone-50"
                            >
                                <Copy className="w-3.5 h-3.5" /> Duplicate
                            </button>
                            <button
                                onClick={() => { setShowMenu(false); onDelete(agent.agent_id); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Agent icon + name */}
            <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-purple-50 border border-violet-200 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-violet-600" />
                </div>
                <div className="min-w-0">
                    <h3 className="text-sm font-bold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {agent.name}
                    </h3>
                    <p className="text-xs text-stone-500 line-clamp-2 mt-0.5">
                        {agent.description || 'No description'}
                    </p>
                </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 text-[10px] text-stone-400 pt-3 border-t border-stone-100">
                <span className="flex items-center gap-1">
                    <Zap className="w-3 h-3" /> {agent.total_runs || 0} runs
                </span>
                <span className="flex items-center gap-1">
                    v{agent.version || 1}
                </span>
                <span className="flex items-center gap-1">
                    {agent.enabled_tools?.length || 0} tools
                </span>
            </div>
        </div>
    );
}

export default function Agents() {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    useEffect(() => {
        loadAgents();
    }, []);

    const loadAgents = async () => {
        try {
            const res = await axios.get(`${API}/agents`, { headers: getAuthHeader() });
            setAgents(res.data.agents || []);
        } catch (err) {
            toast.error('Failed to load agents');
        } finally {
            setLoading(false);
        }
    };

    const createAgent = async () => {
        try {
            const res = await axios.post(`${API}/agents`, {
                name: 'New Agent',
                description: '',
            }, { headers: getAuthHeader() });
            navigate(`/agents/${res.data.agent_id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create agent');
        }
    };

    const duplicateAgent = async (agentId) => {
        try {
            const res = await axios.post(`${API}/agents/${agentId}/duplicate`, {}, { headers: getAuthHeader() });
            toast.success('Agent duplicated');
            loadAgents();
        } catch (err) {
            toast.error('Failed to duplicate agent');
        }
    };

    const deleteAgent = async (agentId) => {
        if (!window.confirm('Delete this agent? This cannot be undone.')) return;
        try {
            await axios.delete(`${API}/agents/${agentId}`, { headers: getAuthHeader() });
            toast.success('Agent deleted');
            setAgents(prev => prev.filter(a => a.agent_id !== agentId));
        } catch (err) {
            toast.error('Failed to delete agent');
        }
    };

    const filtered = agents.filter(a => {
        if (filterStatus && a.status !== filterStatus) return false;
        if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        AI Agents
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        Build and manage autonomous AI agents for your CRM/ERP workflows
                    </p>
                </div>
                <Button onClick={createAgent} className="gap-2">
                    <Plus className="w-4 h-4" /> New Agent
                </Button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search agents..."
                        className="pl-9"
                    />
                </div>
                <div className="flex items-center gap-1">
                    {['', 'draft', 'sandbox', 'active', 'paused'].map(s => (
                        <button
                            key={s}
                            onClick={() => setFilterStatus(s)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                filterStatus === s
                                    ? 'bg-stone-900 text-white'
                                    : 'text-stone-500 hover:bg-stone-100 hover:text-stone-700'
                            }`}
                        >
                            {s || 'All'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Agent Grid */}
            {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-stone-400">
                    <Bot className="w-10 h-10 mb-3 text-stone-300" />
                    <p className="text-sm font-medium">
                        {agents.length === 0 ? 'No agents yet' : 'No matching agents'}
                    </p>
                    {agents.length === 0 && (
                        <p className="text-xs mt-1">Create your first AI agent to automate CRM workflows</p>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(agent => (
                        <AgentCard
                            key={agent.agent_id}
                            agent={agent}
                            onNavigate={id => navigate(`/agents/${id}`)}
                            onDuplicate={duplicateAgent}
                            onDelete={deleteAgent}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
